import { useEffect, useMemo, useState } from 'react';
import { Edit2, Mail, Phone, Plus, Search, Shield, Trash2, User } from 'lucide-react';
import Modal from '../components/Modal';
import { Checkbox, Input, Select } from '../components/Form';
import api from '../lib/api';
import { useAuthStore } from '../store/authStore';

interface Person {
  id: string;
  name: string;
  document: string;
  type: string;
  roles: string[];
  email?: string;
  phone?: string;
  birthDate?: string;
  observations?: string;
  isActive: boolean;
}

interface UserAccess {
  id: string;
  personId?: string;
  email: string;
  profile: 'ADMIN' | 'DIRETOR' | 'GERENTE' | 'CORRETOR' | 'CAPTADOR';
  isActive: boolean;
  hasPaymentPin?: boolean;
  canDeleteCharges?: boolean;
}

const formatProfileLabel = (profile?: string) => {
  if (profile === 'ADMIN') return 'SUPERADMIN';
  return profile || 'SEM PERFIL';
};

const roleLabels: Record<string, string> = {
  TENANT: 'Inquilino',
  OWNER: 'Proprietario',
  BROKER: 'Corretor',
  CORRETOR: 'Corretor',
  CAPTADOR: 'Captador',
  FUNCIONARIO: 'Funcionario',
  GUARANTOR: 'Fiador',
  BUYER: 'Comprador',
  SELLER: 'Vendedor',
  CLIENTE: 'Cliente',
  PROPRIETARIO: 'Proprietario',
  INQUILINO: 'Inquilino',
};

const roleColors: Record<string, string> = {
  TENANT: 'bg-blue-100 text-blue-800',
  OWNER: 'bg-green-100 text-green-800',
  BROKER: 'bg-purple-100 text-purple-800',
  CORRETOR: 'bg-indigo-100 text-indigo-800',
  CAPTADOR: 'bg-orange-100 text-orange-800',
  FUNCIONARIO: 'bg-slate-100 text-slate-800',
  GUARANTOR: 'bg-yellow-100 text-yellow-800',
  BUYER: 'bg-cyan-100 text-cyan-800',
  SELLER: 'bg-pink-100 text-pink-800',
  CLIENTE: 'bg-emerald-100 text-emerald-800',
  PROPRIETARIO: 'bg-lime-100 text-lime-800',
  INQUILINO: 'bg-sky-100 text-sky-800',
};

const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/;
const pinRegex = /^\d{6}$/;

export default function PeoplePage() {
  const { user } = useAuthStore();
  const [people, setPeople] = useState<Person[]>([]);
  const [usersByPersonId, setUsersByPersonId] = useState<Record<string, UserAccess>>({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<string>('ALL');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState({
    name: '',
    document: '',
    type: 'INDIVIDUAL',
    roles: [] as string[],
    email: '',
    phone: '',
    birthDate: '',
    observations: '',
    isActive: true,
    createAccess: false,
    accessEmail: '',
    accessProfile: 'CORRETOR' as UserAccess['profile'],
    accessPassword: '',
    accessPin: '',
    accessCanDeleteCharges: false,
  });

  const canManageUserAccess = useMemo(() => user?.profile === 'ADMIN', [user?.profile]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [peopleRes, usersRes] = await Promise.all([
        api.get('/people?limit=1000'),
        api.get('/users?limit=500'),
      ]);

      const peopleData: Person[] = peopleRes.data?.data || [];
      const usersData: UserAccess[] = usersRes.data?.data || [];

      const nextUsersByPersonId: Record<string, UserAccess> = {};
      for (const user of usersData) {
        if (user.personId) nextUsersByPersonId[user.personId] = user;
      }

      setPeople(peopleData);
      setUsersByPersonId(nextUsersByPersonId);
    } catch (error) {
      console.error('Erro ao carregar pessoas:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredPeople = useMemo(() => {
    return people
      .filter((person) => filterRole === 'ALL' || person.roles?.includes(filterRole))
      .filter(
        (person) =>
          person.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          person.document?.includes(searchTerm.replace(/\D/g, '')),
      );
  }, [people, filterRole, searchTerm]);

  const formatDocument = (document: string) => {
    const clean = (document || '').replace(/\D/g, '');
    if (clean.length === 11) {
      return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    if (clean.length === 14) {
      return clean.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return document;
  };

  const resetForm = () => {
    setSelectedPerson(null);
    setErrors({});
    setFormData({
      name: '',
      document: '',
      type: 'INDIVIDUAL',
      roles: [],
      email: '',
      phone: '',
      birthDate: '',
      observations: '',
      isActive: true,
      createAccess: false,
      accessEmail: '',
      accessProfile: 'CORRETOR',
      accessPassword: '',
      accessPin: '',
      accessCanDeleteCharges: false,
    });
  };

  const openModal = (person?: Person) => {
    if (!person) {
      resetForm();
      setShowModal(true);
      return;
    }

    const linkedUser = usersByPersonId[person.id];
    setSelectedPerson(person);
    setErrors({});
    setFormData({
      name: person.name,
      document: person.document,
      type: person.type,
      roles: person.roles || [],
      email: person.email || '',
      phone: person.phone || '',
      birthDate: person.birthDate ? person.birthDate.slice(0, 10) : '',
      observations: person.observations || '',
      isActive: person.isActive,
      createAccess: canManageUserAccess ? Boolean(linkedUser) : false,
      accessEmail: linkedUser?.email || person.email || '',
      accessProfile: linkedUser?.profile || 'CORRETOR',
      accessPassword: '',
      accessPin: '',
      accessCanDeleteCharges: linkedUser?.canDeleteCharges || false,
    });
    setShowModal(true);
  };

  const validateForm = () => {
    const nextErrors: Record<string, string> = {};

    if (!formData.name.trim()) nextErrors.name = 'Nome e obrigatorio';
    if (!formData.document.trim()) {
      nextErrors.document = 'CPF/CNPJ e obrigatorio';
    } else {
      const clean = formData.document.replace(/\D/g, '');
      if (clean.length !== 11 && clean.length !== 14) {
        nextErrors.document = 'CPF deve ter 11 digitos ou CNPJ 14 digitos';
      }
    }

    if (formData.roles.length === 0) nextErrors.roles = 'Selecione ao menos um papel';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      nextErrors.email = 'E-mail invalido';
    }

    if (formData.createAccess && canManageUserAccess) {
      if (!formData.accessEmail.trim()) {
        nextErrors.accessEmail = 'E-mail de acesso e obrigatorio';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.accessEmail)) {
        nextErrors.accessEmail = 'E-mail de acesso invalido';
      }

      const linkedUser = selectedPerson ? usersByPersonId[selectedPerson.id] : undefined;
      const creatingNewAccess = !linkedUser;
      if (creatingNewAccess && !formData.accessPassword) {
        nextErrors.accessPassword = 'Senha obrigatoria para criar acesso';
      } else if (
        formData.accessPassword &&
        !passwordRegex.test(formData.accessPassword)
      ) {
        nextErrors.accessPassword =
          'Senha minima 8 com maiuscula, minuscula, numero e especial';
      }

      if (creatingNewAccess && !formData.accessPin) {
        nextErrors.accessPin = 'PIN de 6 digitos e obrigatorio';
      } else if (formData.accessPin && !pinRegex.test(formData.accessPin)) {
        nextErrors.accessPin = 'PIN deve conter 6 digitos';
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleRoleChange = (role: string) => {
    setFormData((prev) => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter((item) => item !== role)
        : [...prev.roles, role],
    }));
  };

  const saveAccessUser = async (personId: string) => {
    if (!formData.createAccess || !canManageUserAccess) return;

    const linkedUser = usersByPersonId[personId];

    if (linkedUser) {
      await api.patch(`/users/${linkedUser.id}`, {
        personId,
        name: formData.name.trim(),
        email: formData.accessEmail.trim().toLowerCase(),
        profile: formData.accessProfile,
        canDeleteCharges: formData.accessCanDeleteCharges,
        ...(formData.accessPassword ? { password: formData.accessPassword } : {}),
        ...(formData.accessPin ? { paymentPin: formData.accessPin } : {}),
      });
      return;
    }

    await api.post('/users', {
      personId,
      name: formData.name.trim(),
      email: formData.accessEmail.trim().toLowerCase(),
      password: formData.accessPassword,
      paymentPin: formData.accessPin,
      profile: formData.accessProfile,
      canDeleteCharges: formData.accessCanDeleteCharges,
      document: formData.document.replace(/\D/g, ''),
      phone: formData.phone || undefined,
      isActive: formData.isActive,
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateForm()) return;

    try {
      const personPayload = {
        name: formData.name.trim(),
        document: formData.document.replace(/\D/g, ''),
        type: formData.type,
        roles: formData.roles,
        email: formData.email.trim().toLowerCase() || undefined,
        phone: formData.phone.trim() || undefined,
        birthDate: formData.birthDate || null,
        observations: formData.observations || undefined,
        isActive: formData.isActive,
      };

      let personId = selectedPerson?.id;
      if (selectedPerson) {
        const response = await api.put(`/people/${selectedPerson.id}`, personPayload);
        personId = response.data?.id || selectedPerson.id;
      } else {
        const response = await api.post('/people', personPayload);
        personId = response.data?.id;
      }

      if (personId && formData.createAccess && canManageUserAccess) {
        await saveAccessUser(personId);
      }

      await loadData();
      setShowModal(false);
      resetForm();
    } catch (error: any) {
      console.error('Erro ao salvar pessoa:', error);
      setErrors({
        submit:
          error?.response?.data?.message ||
          'Erro ao salvar pessoa/acesso. Verifique os dados.',
      });
    }
  };

  const handleDelete = async () => {
    if (!selectedPerson) return;
    try {
      await api.delete(`/people/${selectedPerson.id}`);
      setShowDeleteModal(false);
      await loadData();
      resetForm();
    } catch (error) {
      console.error('Erro ao excluir pessoa:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Pessoas (Cadastro Mestre)</h1>
        <button onClick={() => openModal()} className="btn-primary flex items-center space-x-2">
          <Plus className="w-5 h-5" />
          <span>Nova Pessoa</span>
        </button>
      </div>

      <div className="card space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nome ou CPF/CNPJ..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            className="input pl-10"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          {['ALL', 'TENANT', 'OWNER', 'CORRETOR', 'CAPTADOR', 'FUNCIONARIO'].map((role) => (
            <button
              key={role}
              onClick={() => setFilterRole(role)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterRole === role
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {role === 'ALL' ? 'Todos' : roleLabels[role]}
            </button>
          ))}
        </div>
      </div>

      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Nome</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Documento</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Papeis</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Contato</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Acesso</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-700">Acoes</th>
              </tr>
            </thead>
            <tbody>
              {filteredPeople.map((person) => {
                const linkedUser = usersByPersonId[person.id];
                return (
                  <tr key={person.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-primary-600" />
                        </div>
                        <div>
                          <p className="font-medium">{person.name}</p>
                          <p className="text-xs text-gray-500">
                            {person.isActive ? 'Ativo' : 'Inativo'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 font-mono text-sm text-gray-600">
                      {formatDocument(person.document)}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-wrap gap-1">
                        {person.roles?.map((role) => (
                          <span
                            key={role}
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              roleColors[role] || 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {roleLabels[role] || role}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="space-y-1 text-sm text-gray-600">
                        {person.email && (
                          <div className="flex items-center">
                            <Mail className="w-4 h-4 mr-2" />
                            {person.email}
                          </div>
                        )}
                        {person.phone && (
                          <div className="flex items-center">
                            <Phone className="w-4 h-4 mr-2" />
                            {person.phone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {linkedUser ? (
                        <div className="text-xs space-y-1">
                          <div className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-100 text-emerald-700">
                            <Shield className="w-3 h-3" />
                            {formatProfileLabel(linkedUser.profile)}
                          </div>
                          <p className="text-gray-500">{linkedUser.email}</p>
                          <p className="text-gray-500">
                            PIN: {linkedUser.hasPaymentPin ? 'Configurado' : 'Nao configurado'}
                          </p>
                        </div>
                      ) : (
                        <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700">
                          Sem acesso
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => openModal(person)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedPerson(person);
                            setShowDeleteModal(true);
                          }}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Excluir"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={selectedPerson ? 'Editar Pessoa' : 'Nova Pessoa'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nome completo"
              value={formData.name}
              onChange={(event) => setFormData((prev) => ({ ...prev, name: event.target.value }))}
              error={errors.name}
              required
            />
            <Input
              label="CPF/CNPJ"
              value={formData.document}
              onChange={(event) =>
                setFormData((prev) => ({ ...prev, document: event.target.value }))
              }
              error={errors.document}
              required
            />
            <Select
              label="Tipo de pessoa"
              value={formData.type}
              onChange={(event) => setFormData((prev) => ({ ...prev, type: event.target.value }))}
              options={[
                { value: 'INDIVIDUAL', label: 'Pessoa Fisica' },
                { value: 'COMPANY', label: 'Pessoa Juridica' },
              ]}
              required
            />
            <Input
              label="Data de nascimento"
              type="date"
              value={formData.birthDate}
              onChange={(event) =>
                setFormData((prev) => ({ ...prev, birthDate: event.target.value }))
              }
            />
            <Input
              label="E-mail"
              type="email"
              value={formData.email}
              onChange={(event) => setFormData((prev) => ({ ...prev, email: event.target.value }))}
              error={errors.email}
            />
            <Input
              label="Telefone"
              value={formData.phone}
              onChange={(event) => setFormData((prev) => ({ ...prev, phone: event.target.value }))}
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Papeis</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[
                'TENANT',
                'OWNER',
                'CORRETOR',
                'CAPTADOR',
                'FUNCIONARIO',
                'GUARANTOR',
                'CLIENTE',
                'PROPRIETARIO',
              ].map((role) => (
                <Checkbox
                  key={role}
                  label={roleLabels[role] || role}
                  checked={formData.roles.includes(role)}
                  onChange={() => handleRoleChange(role)}
                />
              ))}
            </div>
            {errors.roles && <p className="text-sm text-red-600">{errors.roles}</p>}
          </div>

          <Input
            label="Observacoes"
            value={formData.observations}
            onChange={(event) =>
              setFormData((prev) => ({ ...prev, observations: event.target.value }))
            }
          />

          <Checkbox
            label="Pessoa ativa"
            checked={formData.isActive}
            onChange={(event) =>
              setFormData((prev) => ({ ...prev, isActive: event.target.checked }))
            }
          />

          <div className="rounded-lg border p-4 space-y-4">
            {canManageUserAccess ? (
              <>
                <Checkbox
                  label="Criar/Atualizar acesso ao sistema para esta pessoa"
                  checked={formData.createAccess}
                  onChange={(event) =>
                    setFormData((prev) => ({ ...prev, createAccess: event.target.checked }))
                  }
                />

                {formData.createAccess && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Usuario (e-mail de login)"
                      type="email"
                      value={formData.accessEmail}
                      onChange={(event) =>
                        setFormData((prev) => ({ ...prev, accessEmail: event.target.value }))
                      }
                      error={errors.accessEmail}
                      required
                    />
                    <Select
                      label="Perfil de acesso"
                      value={formData.accessProfile}
                      onChange={(event) =>
                        setFormData((prev) => ({
                          ...prev,
                          accessProfile: event.target.value as UserAccess['profile'],
                        }))
                      }
                      options={[
                        { value: 'ADMIN', label: 'SUPERADMIN' },
                        { value: 'DIRETOR', label: 'DIRETOR' },
                        { value: 'GERENTE', label: 'GERENTE' },
                        { value: 'CORRETOR', label: 'CORRETOR' },
                        { value: 'CAPTADOR', label: 'CAPTADOR' },
                      ]}
                      required
                    />
                    <Checkbox
                      label="Pode apagar cobranças (exige PIN financeiro)"
                      checked={formData.accessCanDeleteCharges}
                      onChange={(event) =>
                        setFormData((prev) => ({
                          ...prev,
                          accessCanDeleteCharges: event.target.checked,
                        }))
                      }
                    />
                    <Input
                      label="Senha (min 8, forte)"
                      type="password"
                      value={formData.accessPassword}
                      onChange={(event) =>
                        setFormData((prev) => ({ ...prev, accessPassword: event.target.value }))
                      }
                      error={errors.accessPassword}
                      placeholder="Use maiuscula, minuscula, numero e especial"
                    />
                    <Input
                      label="PIN financeiro (6 digitos)"
                      type="password"
                      value={formData.accessPin}
                      onChange={(event) =>
                        setFormData((prev) => ({ ...prev, accessPin: event.target.value }))
                      }
                      error={errors.accessPin}
                      placeholder="Ex: 123456"
                    />
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-amber-700">
                Somente SUPERADMIN pode criar/alterar perfis de acesso.
              </p>
            )}
          </div>

          {errors.submit && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{errors.submit}</p>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => setShowModal(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button type="submit" className="btn-primary">
              {selectedPerson ? 'Salvar alteracoes' : 'Cadastrar pessoa'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Confirmar exclusao"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Deseja excluir <strong>{selectedPerson?.name}</strong>?
          </p>
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setShowDeleteModal(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleDelete}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Excluir
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
